import React, { useEffect, useState } from 'react';
import { useAppDispatch } from 'hooks';

import { useSearch } from '~libs/searchParams';

import { Button, ClickableIcon, ExportDialog, Toolbar } from '~components';
import { SearchBar } from '~components/form-fields/search';
import Icon, { faFileCsv, faMagnifyingGlass, faPlus } from '~components/icon';
import { TableView } from '~components/table-view-v3';

import exportFields from '~support-at-home/forms/reconciliations/export';
import columnsFactory from '~support-at-home/forms/reconciliations/list';
import { searchFields } from '~support-at-home/forms/reconciliations/search';

import { getAll as getAllClients } from '~main/actions/clients';
import { setTitle } from '~main/actions/login';
import {
  exportAll,
  useGetReconciliationsQuery,
  useLazyGetReconciliationItemsEstimateQuery,
} from '~support-at-home/actions/reconciliations';

import EditReconModal from './EditRecon';
import ReconciliationsWizard from './reconciliations-wizard';

const Reconciliations: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const dispatch = useAppDispatch();

  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearch('reconciliations-search', {});
  const search = (values: Record<string, any>) => setSearchParams(values);

  const [wizardOpen, setWizardOpen] = useState<boolean>(false);
  const [editReconModalOpen, setEditReconModalOpen] = useState<boolean>(false);
  const [editReconId, setEditReconId] = useState<string>('');
  const [exportDialogOpen, setExportDialogOpen] = useState<boolean>(false);

  useEffect(() => {
    if (isActive) {
      setTitle('Reconciliations');
      dispatch(getAllClients());
    }
  }, [isActive]);

  // RTK Hook
  const { data: reconciliations = [], isFetching: isLoading, refetch } = useGetReconciliationsQuery(searchParams);
  const [getReconciliationItemsTrigger, { data: estimate }] = useLazyGetReconciliationItemsEstimateQuery();

  const download = (params: { [key: string]: any }, onProgress: (value: any) => void) =>
    exportAll({ params, onProgress });

  const toggleEditRecon = (toggle = true, id = '') => {
    setEditReconId(id);
    setEditReconModalOpen(toggle);
    if (!toggle) refetch();
  };

  const columns = columnsFactory((id) => toggleEditRecon(true, id));

  return (
    <div className="p-2 reconciliations">
      <Toolbar
        id="reconciliations"
        rightCluster={
          <>
            <ClickableIcon
              icon={faFileCsv}
              id="reconciliations-file-export-button"
              onClick={() => setExportDialogOpen(!exportDialogOpen)}
              toolTip="Export Reconciliations"
            />
            <ClickableIcon
              className="me-2"
              icon={faMagnifyingGlass}
              id="reconciliations-search-button"
              onClick={() => setSearchBarOpen(!searchBarOpen)}
              toolTip="Toggle Search Bar"
            />
            <Button dataCy="reconciliations-new-button" size="sm" color="success" onClick={() => setWizardOpen(true)}>
              <Icon className="me-2" icon={faPlus} />
              New
            </Button>
          </>
        }
      />
      <SearchBar isOpen={searchBarOpen} fields={searchFields} initialValues={searchParams} onSubmit={search} />
      <TableView
        id="reconciliations-table"
        columns={columns}
        data={reconciliations}
        isLoading={isLoading}
        emptyMessage="No reconciliations found"
        readonly
        striped
      />
      <ReconciliationsWizard isOpen={wizardOpen} setIsOpen={setWizardOpen} />
      <EditReconModal isOpen={editReconModalOpen} setIsOpen={toggleEditRecon} id={editReconId} />
      <ExportDialog
        isOpen={exportDialogOpen}
        fields={exportFields}
        openingValues={searchParams}
        estimate={estimate}
        onClose={() => setExportDialogOpen(false)}
        onEstimate={async (params) => ({ payload: await getReconciliationItemsTrigger(params) })}
        onDownload={download}
      />
    </div>
  );
};

export default Reconciliations;
